import { EventType } from "@/analytics/analytics-events";
import { useAppSelector } from "@/store/store-hooks";
import { selectIsHbToSxgMigrated } from "@/store/subscription-features/subscription-features-selectors";
import { useWasOpenedFromWebEditor } from "@/utils/web-editor-redirect";
import { WelcomeDialog } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import type { JSX } from "react";
import { useCallback, useMemo } from "react";
import { useWasWelcomedAfterHbToSxgMigration } from "./use-was-welcomed-after-hb-to-sxg-migration";

/**
 * @returns the welcome dialog for the users of a migrated workspace who were redirected from the
 *  HoloBuilder WebEditor, shown once per account or on every logged-out redirect
 */
export function HbToSxgWelcomeDialog(): JSX.Element | null {
  // Always called, as it also drops the redirect marker from the url
  const wasOpenedFromWebEditor = useWasOpenedFromWebEditor();
  const isHbToSxgMigrated = useAppSelector(selectIsHbToSxgMigrated);

  return useMemo(
    () =>
      wasOpenedFromWebEditor && isHbToSxgMigrated ? (
        <FirstTimeWelcomeDialog />
      ) : null,
    [wasOpenedFromWebEditor, isHbToSxgMigrated],
  );
}

/** @returns the welcome dialog, until the user has been welcomed */
function FirstTimeWelcomeDialog(): JSX.Element {
  const { isLoaded, wasWelcomed, markAsWelcomed } =
    useWasWelcomedAfterHbToSxgMigration();

  const onLearnMoreClicked = useCallback(() => {
    Analytics.track(EventType.clickOnHbToSxgMigrationLearnMore);
  }, []);

  return (
    <WelcomeDialog
      open={isLoaded && !wasWelcomed}
      onLearnMoreClicked={onLearnMoreClicked}
      onClose={markAsWelcomed}
    />
  );
}
